/**
 * Single debug/diagnostics gate for the client.
 *
 * Every diagnostic path (perf overlay, debug UI, etc.) is gated by this one
 * flag so a production build with the flag off pays nothing more than a
 * boolean read on the hot path.
 */

const STORAGE_KEY = 'sail.debug';

/** Resolve the initial debug state from the URL, then persisted storage. */
function resolveInitialDebug(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    // `?debug` or `?debug=1` enables; `?debug=0` explicitly disables.
    if (params.has('debug')) return params.get('debug') !== '0';
  } catch {
    // Ignore malformed query strings and fall through to localStorage.
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // localStorage may be unavailable (private mode); default to off.
    return false;
  }
}

/**
 * True when diagnostics are enabled (`?debug=1`/`?debug` in the URL, or a
 * persisted `localStorage['sail.debug'] = '1'`).
 */
export let DEBUG_ENABLED: boolean = resolveInitialDebug();

/** True when diagnostics are enabled. */
export function isDebug(): boolean {
  return DEBUG_ENABLED;
}

/**
 * Enable/disable diagnostics. Persists to localStorage; persistence is a
 * no-op before the DOM is available.
 */
export function setDebug(enabled: boolean): void {
  DEBUG_ENABLED = enabled;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Persistence is best-effort; the in-memory flag still toggles.
  }
}

/** Weather override for diagnostics (`?weather=`). */
export type DebugWeatherMode = 'calm' | 'storm' | 'server';

/** Explicit `?weather=` value, or null when absent or unrecognised. */
function resolveWeatherParam(): DebugWeatherMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get('weather');
    if (value === 'calm' || value === 'storm' || value === 'server') return value;
  } catch {
    // Ignore malformed query strings.
  }
  return null;
}

const URL_WEATHER = resolveWeatherParam();

/**
 * Weather override for diagnostics. An explicit `?weather=calm|storm|server`
 * always wins; otherwise diagnostics hold the sky in `calm` so good-weather
 * work isn't confounded by the storm cycle, and `server` when debug is off.
 */
export function getDebugWeatherMode(): DebugWeatherMode {
  if (URL_WEATHER !== null) return URL_WEATHER;
  return DEBUG_ENABLED ? 'calm' : 'server';
}
